import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useActivities, useActivityMutations, useLeadMutations } from '../hooks/useCRM';
import { getActivitiesCalendar, getActivitiesWeek } from '../utils/crmApi';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import SmartActionsBar from '../components/SmartActionsBar';
import '../crm.css';
import { contactPath, dealPath, leadPath } from '../utils/crmEnv';
import {
    pad2,
    dayKeyFromLocalDateObj,
    parseDayKeyToLocalDate,
    combineLocalDateAndTime,
    formatTimeLocal,
    getTzOffsetMinutes,
} from '../utils/calendarDateUtils';

const TYPE_OPTIONS = [
    { value: 'call', label: 'Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email', label: 'Email' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'task', label: 'Task' },
    { value: 'site_visit', label: 'Site Visit' },
    { value: 'note', label: 'Note' },
];

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
];

const CHANNEL_OPTIONS = [
    { value: 'phone', label: 'Phone' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email', label: 'Email' },
    { value: 'in_person', label: 'In Person' },
    { value: 'other', label: 'Other' },
];

const OUTCOME_OPTIONS = [
    { value: '', label: 'No outcome' },
    { value: 'no_answer', label: 'No Answer' },
    { value: 'interested', label: 'Interested' },
    { value: 'not_interested', label: 'Not Interested' },
    { value: 'callback', label: 'Callback' },
    { value: 'site_visit_booked', label: 'Site Visit Booked' },
    { value: 'deal_closed', label: 'Deal Closed' },
    { value: 'other', label: 'Other' },
];

const REMINDER_OPTIONS = [
    { value: '', label: 'No reminder' },
    { value: '30m', label: '30 minutes before' },
    { value: '2h', label: '2 hours before' },
    { value: '1d', label: '1 day before' },
];

const ENTITY_OPTIONS = [
    { value: 'lead', label: 'Lead' },
    { value: 'contact', label: 'Contact' },
    { value: 'deal', label: 'Deal' },
];

const TEMPLATES = [
    {
        id: 'site-visit',
        label: 'Site visit confirmation',
        title: 'Site visit confirmed',
        description: 'Hi {name}, your site visit for {project} is confirmed on {date} at {time}.',
    },
    {
        id: 'payment-reminder',
        label: 'Payment reminder',
        title: 'Payment reminder',
        description: 'Hi {name}, this is a reminder for the upcoming payment related to {project}.',
    },
    {
        id: 'documents-needed',
        label: 'Documents needed',
        title: 'Documents required',
        description: 'Hi {name}, please share the following documents to proceed with {project}.',
    },
    {
        id: 'checking-in',
        label: 'Checking in',
        title: 'Checking in',
        description: 'Hi {name}, just checking in about your interest in {project}.',
    },
];

const TYPE_ICONS = {
    call: 'CALL',
    whatsapp: 'WA',
    email: 'EMAIL',
    meeting: 'MEET',
    task: 'TASK',
    site_visit: 'VISIT',
    note: 'NOTE',
};

const ActivitiesPage = () => {
    const history = useHistory();
    const {
        activities,
        loading,
        refetch,
        updateParams,
    } = useActivities({
        sort: 'dueDate',
        dir: 'asc',
    });
    const { create, update, remove, loading: mutationLoading } = useActivityMutations();
    const { executeAction } = useLeadMutations();
    const { showSuccess, showError } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();

    const [showModal, setShowModal] = useState(false);
    const [editingActivity, setEditingActivity] = useState(null);
    const [modalTab, setModalTab] = useState('log');
    const [activeTab, setActiveTab] = useState('due');
    const [searchInput, setSearchInput] = useState('');
    const [rescheduleOpenId, setRescheduleOpenId] = useState(null);

    const [prefillDate, setPrefillDate] = useState(null);
    const [completedRange, setCompletedRange] = useState('today');

    const [formState, setFormState] = useState({
        type: 'call',
        title: '',
        description: '',
        entityType: 'lead',
        entityId: '',
        entityName: '',
        dueDate: '',
        dueTime: '',
        priority: 'medium',
        channel: 'phone',
        outcome: '',
        reminder: '',
        metaProject: '',
        metaPhone: '',
        templateId: '',
        status: 'pending',
    });
    const [formErrors, setFormErrors] = useState({});
    const [smartActionActivity, setSmartActionActivity] = useState(null);
    const [showSmartFollowupModal, setShowSmartFollowupModal] = useState(false);
    const [showSmartSiteVisitModal, setShowSmartSiteVisitModal] = useState(false);
    const [smartFollowupDraft, setSmartFollowupDraft] = useState({ date: '', time: '' });
    const [smartSiteVisitDraft, setSmartSiteVisitDraft] = useState({ date: '', time: '', location: '', attendees: '', notes: '' });
    const tzOffsetMinutes = useMemo(() => getTzOffsetMinutes(), []);
    const [calendarView, setCalendarView] = useState('month');
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
    });
    const todayDayKey = useMemo(() => dayKeyFromLocalDateObj(new Date()), []);
    const [activeDayKey, setActiveDayKey] = useState(() => dayKeyFromLocalDateObj(new Date()));
    const [calendarData, setCalendarData] = useState({ month: calendarMonth, days: {} });
    const [weekData, setWeekData] = useState({ start: activeDayKey, days: {} });
    const [calendarLoading, setCalendarLoading] = useState(false);
    const calendarCacheRef = useRef(new Map());
    const [agendaOpen, setAgendaOpen] = useState(false);
    const isMobileCalendar = useMemo(() => window.matchMedia('(max-width: 1100px)').matches, []);
    const dragActivityRef = useRef(null);

    const inferActivityKind = (activity) => {
        if (!activity) return 'log';
        if (activity.kind === 'followup') return 'scheduled';
        if (activity.kind) return activity.kind;
        if (activity.dueAt || activity.dueDate || activity.dueTime) return 'scheduled';
        return 'log';
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            updateParams({ q: searchInput });
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchInput, updateParams]);



    useEffect(() => {
        if (!editingActivity) {
            const prefill = prefillDate ? new Date(prefillDate) : null;
            const dueDateValue = prefill ? dayKeyFromLocalDateObj(prefill) : '';
            const dueTimeValue = prefill ? '10:00' : '';
            setFormState({
                type: 'call',
                title: '',
                description: '',
                entityType: 'lead',
                entityId: '',
                entityName: '',
                dueDate: dueDateValue,
                dueTime: dueTimeValue,
                priority: 'medium',
                channel: 'phone',
                outcome: '',
                reminder: '',
                metaProject: '',
                metaPhone: '',
                templateId: '',
                status: 'pending',
            });
            setFormErrors({});
            return;
        }

        const dueMs = getDueAtMs(editingActivity);
        const due = dueMs ? new Date(dueMs) : null;
        const meta = editingActivity.meta || {};
        setFormState({
            type: editingActivity.type || 'call',
            title: editingActivity.title || '',
            description: editingActivity.description || '',
            entityType: editingActivity.entityType || 'lead',
            entityId: editingActivity.entityId || '',
            entityName: editingActivity.entityName || '',
            dueDate: due ? dayKeyFromLocalDateObj(due) : '',
            dueTime: due ? due.toTimeString().slice(0, 5) : '',
            priority: editingActivity.priority || 'medium',
            channel: editingActivity.channel || 'phone',
            outcome: editingActivity.outcome || '',
            reminder: '',
            metaProject: meta.project || '',
            metaPhone: meta.phone || '',
            templateId: meta.templateId || '',
            status: editingActivity.status || 'pending',
        });
        setFormErrors({});
    }, [editingActivity, prefillDate]);

    useEffect(() => {
        if (!showModal) {
            setPrefillDate(null);
        }
    }, [showModal]);




    const parseLocalDateTime = (dateString, timeString) => {
        if (!dateString) return null;
        if (dateString.includes('T')) {
            const parsed = new Date(dateString);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }
        const [year, month, day] = dateString.split('-').map(Number);
        if (!year || !month || !day) return null;
        const [hours, minutes] = (timeString || '00:00').split(':').map(Number);
        return new Date(year, month - 1, day, hours || 0, minutes || 0, 0, 0);
    };

    const formatDate = (dateInput, includeTime = true) => {
        if (!dateInput && dateInput !== 0) return '';
        const date = dateInput instanceof Date
            ? dateInput
            : typeof dateInput === 'number'
                ? new Date(dateInput)
                : new Date(dateInput);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: includeTime ? 'numeric' : undefined,
            minute: includeTime ? '2-digit' : undefined,
            hour12: true,
        });
    };

    const formatDayLabel = (date) =>
        date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    const formatAgendaTitle = (value) => {
        if (!value) return 'Select a day';
        return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
    };

    const getDueAtMs = (activity) => {
        if (!activity) return null;
        if (typeof activity.dueAt === 'number') return activity.dueAt;
        if (activity.dueAt) {
            const parsed = new Date(activity.dueAt);
            return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
        }
        if (!activity.dueDate) return null;
        const parsed = parseLocalDateTime(activity.dueDate, activity.dueTime || '');
        return parsed ? parsed.getTime() : null;
    };

    const getCompletedAtMs = (activity) => {
        if (!activity) return null;
        if (typeof activity.completedAt === 'number') return activity.completedAt;
        if (activity.completedAt) {
            const parsed = new Date(activity.completedAt);
            return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
        }
        return null;
    };

    const followupActivities = useMemo(
        () => (activities || []).filter((activity) => inferActivityKind(activity) === 'scheduled'),
        [activities],
    );

    const getActivityTimeMs = (activity) => {
        if (!activity) return null;
        const dueAt = getDueAtMs(activity);
        if (dueAt) return dueAt;
        const completedAt = getCompletedAtMs(activity);
        if (completedAt) return completedAt;
        if (activity.occurredAt) {
            const parsed = new Date(activity.occurredAt);
            if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
        }
        if (activity.createdAt) {
            const parsed = new Date(activity.createdAt);
            if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
        }
        return null;
    };

    const formatTimeFromItem = (activity) => {
        const timeMs = getActivityTimeMs(activity);
        if (!timeMs) return '-';
        return new Date(timeMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };

    const getStatusLabel = (activity) => {
        // Use server-provided __bucket for calendar items (single source of truth)
        if (activity?.__bucket) {
            if (activity.__bucket === 'completed' || activity.__bucket === 'done') return 'Completed';
            if (activity.__bucket === 'overdue') return 'Overdue';
            return 'Due';
        }
        // Fallback for non-calendar items (tabbed views)
        if (activity?.status === 'completed') return 'Completed';
        const dueAt = getDueAtMs(activity);
        if (dueAt && dueAt < Date.now()) return 'Overdue';
        return 'Due';
    };

    const normalizePhone = (value) => {
        if (!value) return '';
        return String(value).replace(/[^\d]/g, '');
    };

    const resolveTemplate = (activity) => {
        const templateId = activity?.meta?.templateId;
        if (!templateId) return null;
        return TEMPLATES.find((template) => template.id === templateId) || null;
    };

    const applyTemplate = (text, activity) => {
        if (!text) return '';
        const dueMs = getDueAtMs(activity);
        const due = dueMs ? new Date(dueMs) : null;
        const replacements = {
            name: activity?.entityName || '',
            project: activity?.meta?.project || '',
            date: due ? due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
            time: due ? due.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '',
            phone: activity?.meta?.phone || '',
        };
        return text.replace(/\{(name|project|date|time|phone)\}/g, (_, key) => replacements[key] || '');
    };

    const buildWhatsAppMessage = (activity) => {
        const template = resolveTemplate(activity);
        const baseText = template?.description || activity?.description || activity?.title || 'Hello!';
        return applyTemplate(baseText, activity).trim() || 'Hello!';
    };

    const buildWhatsAppUrl = (activity) => {
        const message = buildWhatsAppMessage(activity);
        const phone = normalizePhone(activity?.meta?.phone);
        const base = phone ? `https://wa.me/${phone}` : 'https://wa.me/';
        return `${base}?text=${encodeURIComponent(message)}`;
    };

    const resolveSmartLeadId = (activity) => (
        activity?.leadId || (activity?.entityType === 'lead' ? activity.entityId : null)
    );

    const formatEntityLabel = (activity) => {
        if (!activity?.entityType) return '';
        if (activity.entityType === 'lead') {
            const name = activity.entityName || 'Lead';
            const shortId = activity.leadShortId
                ? String(activity.leadShortId).toUpperCase()
                : activity.entityId
                    ? String(activity.entityId).slice(-4).toUpperCase()
                    : '';
            return shortId ? `lead: ${name} - ${shortId}` : `lead: ${name}`;
        }
        const name = activity.entityName || activity.entityId || '';
        return `${activity.entityType}: ${name}`.trim();
    };

    const activityBuckets = useMemo(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        const startMs = start.getTime();
        const endMs = end.getTime();
        const nowMs = now.getTime();

        const dueToday = [];
        const upcoming = [];
        const unscheduled = [];
        const overdueToday = [];
        const overdueOlder = [];
        const completedAll = [];

        (followupActivities || []).forEach((activity) => {
            const hasStatus = typeof activity?.status === 'string' && activity.status.length > 0;
            const isCompleted = activity?.status === 'completed' || (!hasStatus && activity?.completed === true);
            const dueAt = getDueAtMs(activity);
            const completedAt = getCompletedAtMs(activity);

            if (isCompleted) {
                completedAll.push({ ...activity, _completedAt: completedAt });
                return;
            }

            if (dueAt === null) {
                unscheduled.push(activity);
                return;
            }

            if (dueAt < startMs) {
                overdueOlder.push(activity);
                return;
            }

            if (dueAt >= startMs && dueAt < nowMs) {
                overdueToday.push(activity);
                return;
            }

            if (dueAt >= nowMs && dueAt < endMs) {
                dueToday.push(activity);
                return;
            }

            if (dueAt >= endMs) {
                upcoming.push(activity);
            }
        });

        return {
            dueToday,
            upcoming,
            unscheduled,
            overdueToday,
            overdueOlder,
            completedAll,
            summary: {
                due: dueToday.length + upcoming.length + unscheduled.length,
                overdue: overdueToday.length + overdueOlder.length,
                completed: completedAll.length,
            },
        };
    }, [followupActivities]);

    const completedFiltered = useMemo(() => {
        const items = activityBuckets.completedAll || [];
        if (completedRange === 'all') {
            return items;
        }
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        if (completedRange === 'today') {
            return items.filter((activity) => {
                const completedAt = activity._completedAt ?? getCompletedAtMs(activity);
                return completedAt !== null && completedAt >= startOfToday;
            });
        }
        if (completedRange === '7') {
            const start = startOfToday - 6 * 24 * 60 * 60 * 1000;
            return items.filter((activity) => {
                const completedAt = activity._completedAt ?? getCompletedAtMs(activity);
                return completedAt !== null && completedAt >= start;
            });
        }
        return items;
    }, [activityBuckets.completedAll, completedRange]);

    const buildSmartTemplateMessage = (templateId, activity) => {
        const name = activity?.entityName || 'there';
        if (templateId === 'intro') {
            return `Hi ${name}, hope you're doing well. Happy to help with your property requirements.`;
        }
        if (templateId === 'options') {
            return `Hi ${name}, I have shortlisted some options. When can we discuss-`;
        }
        if (templateId === 'confirm') {
            return `Hi ${name}, confirming your site visit. Please let me know if the time works for you.`;
        }
        return '';
    };

    const openSmartFollowupModal = (activity) => {
        const leadId = resolveSmartLeadId(activity);
        if (!leadId) {
            showError('Link a lead to use Smart Actions');
            return;
        }
        setSmartActionActivity(activity);
        setSmartFollowupDraft({ date: '', time: '' });
        setShowSmartFollowupModal(true);
    };

    const openSmartSiteVisitModal = (activity) => {
        const leadId = resolveSmartLeadId(activity);
        if (!leadId) {
            showError('Link a lead to use Smart Actions');
            return;
        }
        setSmartActionActivity(activity);
        setSmartSiteVisitDraft({
            date: '',
            time: '',
            location: activity?.location || '',
            attendees: '',
            notes: '',
        });
        setShowSmartSiteVisitModal(true);
    };

    const handleSmartFollowupSave = async () => {
        if (!smartFollowupDraft.date) {
            showError('Select a follow-up date');
            return;
        }
        const leadId = resolveSmartLeadId(smartActionActivity);
        if (!leadId) return;
        const time = smartFollowupDraft.time || '09:00';
        const scheduled = new Date(`${smartFollowupDraft.date}T${time}`);
        if (Number.isNaN(scheduled.getTime())) {
            showError('Invalid follow-up date');
            return;
        }
        try {
            await executeAction(
                leadId,
                { type: 'schedule_follow_up', dueAt: scheduled.getTime() },
                `followup-${leadId}-${scheduled.getTime()}`,
            );
            showSuccess('Follow-up scheduled');
            setShowSmartFollowupModal(false);
            setSmartActionActivity(null);
            refetch();
        } catch (err) {
            showError('Failed to schedule follow-up');
        }
    };

    const handleSmartSiteVisitSave = async () => {
        if (!smartSiteVisitDraft.date) {
            showError('Select a visit date');
            return;
        }
        const leadId = resolveSmartLeadId(smartActionActivity);
        if (!leadId) return;
        const dueAt = new Date(`${smartSiteVisitDraft.date}T${smartSiteVisitDraft.time || '09:00'}`).getTime();
        if (Number.isNaN(dueAt)) {
            showError('Invalid visit date');
            return;
        }
        const noteParts = [];
        if (smartSiteVisitDraft.notes) noteParts.push(smartSiteVisitDraft.notes);
        if (smartSiteVisitDraft.attendees) noteParts.push(`Attendees: ${smartSiteVisitDraft.attendees}`);
        const note = noteParts.join('\n') || undefined;

        try {
            await executeAction(
                leadId,
                {
                    type: 'schedule_site_visit',
                    dueAt,
                    location: smartSiteVisitDraft.location || undefined,
                    note,
                    propertyId: smartActionActivity?.meta?.propertyId,
                    propertyTitle: smartActionActivity?.meta?.propertyTitle,
                },
                `site-visit-${leadId}-${dueAt}`,
            );
            showSuccess('Site visit scheduled');
            setShowSmartSiteVisitModal(false);
            setSmartActionActivity(null);
            refetch();
        } catch (err) {
            showError('Failed to schedule site visit');
        }
    };

    const handleSmartMarkContacted = async (activity) => {
        const leadId = resolveSmartLeadId(activity);
        if (!leadId) {
            showError('Link a lead to use Smart Actions');
            return;
        }
        try {
            await executeAction(leadId, { type: 'mark_contacted' }, `mark-contacted-${leadId}-${activity.id}`);
            showSuccess('Lead marked as contacted');
            refetch();
        } catch (err) {
            showError('Failed to update lead');
        }
    };

    const getModalLeadId = () => (
        formState.entityType === 'lead' ? formState.entityId.trim() : ''
    );

    const buildModalWhatsAppUrl = (phone, message) => (
        phone ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : ''
    );

    const handleModalLogAndOpen = async (channel, message) => {
        const leadId = getModalLeadId();
        if (!leadId) {
            showError('Select a lead to use Smart Actions');
            return;
        }

        const phone = normalizePhone(formState.metaPhone);
        const title = formState.title?.trim() || 'Follow-up';
        const body = message || formState.description?.trim() || title;

        try {
            if (channel === 'call') {
                await executeAction(leadId, { type: 'log_call', note: title }, `smart-modal-call-${leadId}-${Date.now()}`);
            } else if (channel === 'email') {
                await executeAction(leadId, { type: 'send_email', subject: title, body }, `smart-modal-email-${leadId}-${Date.now()}`);
            } else if (channel === 'whatsapp') {
                await executeAction(leadId, { type: 'send_whatsapp', message: body }, `smart-modal-wa-${leadId}-${Date.now()}`);
            }
        } catch (err) {
            showError('Failed to log communication');
        }

        if (channel === 'call' && phone) {
            window.open(`tel:${phone}`, '_self');
        }
        if (channel === 'whatsapp' && phone) {
            window.open(buildModalWhatsAppUrl(phone, body), '_blank', 'noopener,noreferrer');
        }
        refetch();
    };

    const handleModalTemplateSelect = (templateId) => {
        const leadId = getModalLeadId();
        if (!leadId) {
            showError('Select a lead to use templates');
            return;
        }
        const phone = normalizePhone(formState.metaPhone);
        if (!phone) {
            showError('Add a phone number to use templates');
            return;
        }
        const message = buildSmartTemplateMessage(templateId, { entityName: formState.entityName || 'there' });
        if (!message) return;
        handleModalLogAndOpen('whatsapp', message);
    };

    const buildModalSmartActionsProps = () => {
        const leadId = getModalLeadId();
        const phone = normalizePhone(formState.metaPhone);
        const entityId = leadId ? formState.entityId.trim() : '';
        return {
            entityType: formState.entityType,
            entityId,
            lead: { phone: formState.metaPhone },
            disabledReason: entityId ? undefined : 'Select a lead to enable',
            onCall: phone ? () => handleModalLogAndOpen('call') : undefined,
            onWhatsApp: phone ? () => handleModalLogAndOpen('whatsapp') : undefined,
            onEmail: undefined,
            onScheduleFollowUp: leadId
                ? () => openSmartFollowupModal({ leadId, entityType: 'lead', entityId: leadId, entityName: formState.entityName, meta: { phone: formState.metaPhone } })
                : undefined,
            onScheduleSiteVisit: leadId
                ? () => openSmartSiteVisitModal({ leadId, entityType: 'lead', entityId: leadId, entityName: formState.entityName, meta: { phone: formState.metaPhone } })
                : undefined,
            onMarkContacted: leadId
                ? () => handleSmartMarkContacted({ leadId, entityType: 'lead', entityId: leadId, id: 'modal' })
                : undefined,
            onTemplateSelect: handleModalTemplateSelect,
        };
    };

    const getEntityRoute = (activity) => {
        if (activity.entityType === 'lead') return leadPath(activity.entityId);
        if (activity.entityType === 'contact') return contactPath(activity.entityId);
        if (activity.entityType === 'deal') return dealPath(activity.entityId);
        return '';
    };

    const openCreateFollowUp = (date) => {
        if (!date) return;
        setEditingActivity(null);
        setPrefillDate(date);
        setShowModal(true);
    };

    const getMonthDateFromKey = (monthKey) => {
        const [year, month] = String(monthKey || '').split('-').map(Number);
        if (!year || !month) return new Date();
        return new Date(year, month - 1, 1);
    };

    const getWeekStartKey = (dayKey) => {
        const date = parseDayKeyToLocalDate(dayKey);
        const start = new Date(date);
        start.setDate(date.getDate() - date.getDay());
        start.setHours(0, 0, 0, 0);
        return dayKeyFromLocalDateObj(start);
    };

    const cacheKeyForMonth = (monthKey) => `${monthKey}-${tzOffsetMinutes}`;

    const fetchCalendarMonth = useCallback(async (monthKey, { prefetch = false, force = false } = {}) => {
        if (!monthKey) return null;
        const cacheKey = cacheKeyForMonth(monthKey);
        if (!force && calendarCacheRef.current.has(cacheKey)) {
            const cached = calendarCacheRef.current.get(cacheKey);
            if (!prefetch) setCalendarData(cached);
            return cached;
        }
        try {
            if (!prefetch) setCalendarLoading(true);
            const data = await getActivitiesCalendar({ month: monthKey, tzOffsetMinutes });
            calendarCacheRef.current.set(cacheKey, data);
            if (!prefetch) setCalendarData(data);
            return data;
        } catch (error) {
            console.error('Failed to fetch calendar month:', error);
            if (!prefetch) showError('Failed to load calendar');
            return null;
        } finally {
            if (!prefetch) setCalendarLoading(false);
        }
    }, [tzOffsetMinutes, showError]);

    const fetchCalendarWeek = useCallback(async (startKey) => {
        if (!startKey) return null;
        try {
            setCalendarLoading(true);
            const data = await getActivitiesWeek({ start: startKey, tzOffsetMinutes });
            setWeekData(data);
            return data;
        } catch (error) {
            console.error('Failed to fetch calendar week:', error);
            showError('Failed to load calendar');
            return null;
        } finally {
            setCalendarLoading(false);
        }
    }, [tzOffsetMinutes, showError]);

    const getDayBucket = useCallback((dayKey) => {
        if (!dayKey) return { items: [], due: 0, overdue: 0, completed: 0 };
        if (calendarView === 'week' && weekData?.days?.[dayKey]) {
            return weekData.days[dayKey];
        }
        const monthKey = dayKey.slice(0, 7);
        const cached = calendarCacheRef.current.get(cacheKeyForMonth(monthKey));
        if (cached?.days?.[dayKey]) return cached.days[dayKey];
        if (calendarData?.days?.[dayKey]) return calendarData.days[dayKey];
        return { items: [], due: 0, overdue: 0, completed: 0 };
    }, [calendarView, weekData, calendarData]);

    const selectedDayItems = useMemo(
        () => (getDayBucket(activeDayKey)?.items || []).slice().sort((a, b) => (a.__timeMs || 0) - (b.__timeMs || 0)),
        [getDayBucket, activeDayKey],
    );

    useEffect(() => {
        if (activeTab !== 'calendar' || calendarView !== 'month') return;
        // Always refresh active month from server when entering/opening calendar month view.
        // This keeps calendar day/status buckets aligned with Due/Overdue/Completed tabs.
        fetchCalendarMonth(calendarMonth, { force: true });
        const base = getMonthDateFromKey(calendarMonth);
        const prev = new Date(base.getFullYear(), base.getMonth() - 1, 1);
        const next = new Date(base.getFullYear(), base.getMonth() + 1, 1);
        fetchCalendarMonth(`${prev.getFullYear()}-${pad2(prev.getMonth() + 1)}`, { prefetch: true });
        fetchCalendarMonth(`${next.getFullYear()}-${pad2(next.getMonth() + 1)}`, { prefetch: true });
    }, [activeTab, calendarView, calendarMonth, fetchCalendarMonth]);

    useEffect(() => {
        if (activeTab !== 'calendar' || calendarView !== 'week') return;
        const weekStartKey = getWeekStartKey(activeDayKey);
        fetchCalendarWeek(weekStartKey);
    }, [activeTab, calendarView, activeDayKey, fetchCalendarWeek]);

    const handleCalendarToday = () => {
        const now = new Date();
        const dayKey = dayKeyFromLocalDateObj(now);
        setActiveDayKey(dayKey);
        setCalendarMonth(`${now.getFullYear()}-${pad2(now.getMonth() + 1)}`);
        if (isMobileCalendar) setAgendaOpen(true);
    };

    const handleCalendarPrev = () => {
        if (calendarView === 'month') {
            const base = getMonthDateFromKey(calendarMonth);
            const prev = new Date(base.getFullYear(), base.getMonth() - 1, 1);
            setCalendarMonth(`${prev.getFullYear()}-${pad2(prev.getMonth() + 1)}`);
            setActiveDayKey(dayKeyFromLocalDateObj(prev));
            return;
        }
        const current = parseDayKeyToLocalDate(activeDayKey);
        current.setDate(current.getDate() - 7);
        setActiveDayKey(dayKeyFromLocalDateObj(current));
    };

    const handleCalendarNext = () => {
        if (calendarView === 'month') {
            const base = getMonthDateFromKey(calendarMonth);
            const next = new Date(base.getFullYear(), base.getMonth() + 1, 1);
            setCalendarMonth(`${next.getFullYear()}-${pad2(next.getMonth() + 1)}`);
            setActiveDayKey(dayKeyFromLocalDateObj(next));
            return;
        }
        const current = parseDayKeyToLocalDate(activeDayKey);
        current.setDate(current.getDate() + 7);
        setActiveDayKey(dayKeyFromLocalDateObj(current));
    };

    const handleSelectDay = (dayKey) => {
        setActiveDayKey(dayKey);
        if (isMobileCalendar) setAgendaOpen(true);
    };

    const monthGridDays = useMemo(() => {
        const base = getMonthDateFromKey(calendarMonth);
        const first = new Date(base.getFullYear(), base.getMonth(), 1);
        const start = new Date(first);
        start.setDate(first.getDate() - first.getDay());
        start.setHours(0, 0, 0, 0);
        return Array.from({ length: 42 }, (_, idx) => {
            const date = new Date(start);
            date.setDate(start.getDate() + idx);
            return date;
        });
    }, [calendarMonth]);

    const weekDays = useMemo(() => {
        const start = parseDayKeyToLocalDate(getWeekStartKey(activeDayKey));
        return Array.from({ length: 7 }, (_, idx) => {
            const date = new Date(start);
            date.setDate(start.getDate() + idx);
            return date;
        });
    }, [activeDayKey]);

    const weekSlots = useMemo(() => {
        const slots = [];
        for (let hour = 9; hour <= 21; hour += 1) {
            for (let minute = 0; minute < 60; minute += 30) {
                if (hour === 21 && minute > 0) break;
                slots.push(`${pad2(hour)}:${pad2(minute)}`);
            }
        }
        return slots;
    }, []);

    const computeClientBucket = (activity, timeMs) => {
        const hasStatus = typeof activity?.status === 'string' && activity.status.length > 0;
        const isDone = activity?.status === 'completed'
            || (!hasStatus && activity?.completed === true)
            || activity?.kind === 'log'
            || activity?.__bucket === 'done';
        if (isDone) return 'done';
        return timeMs < Date.now() ? 'overdue' : 'due';
    };

    const recountDay = (items = []) => {
        return items.reduce(
            (acc, item) => {
                const bucket = item.__bucket || computeClientBucket(item, item.__timeMs || Date.now());
                if (bucket === 'overdue') acc.overdue += 1;
                else if (bucket === 'due') acc.due += 1;
                else acc.completed += 1;
                return acc;
            },
            { due: 0, overdue: 0, completed: 0 },
        );
    };

    const updateDayInCache = (dayKey, updater) => {
        if (!dayKey) return;
        const monthKey = dayKey.slice(0, 7);
        const cacheKey = cacheKeyForMonth(monthKey);
        const cached = calendarCacheRef.current.get(cacheKey);
        if (!cached) return;
        const day = cached.days?.[dayKey] || { due: 0, overdue: 0, completed: 0, items: [] };
        const nextDay = updater(day);
        const nextMonth = {
            ...cached,
            days: { ...cached.days, [dayKey]: nextDay },
        };
        calendarCacheRef.current.set(cacheKey, nextMonth);
        if (calendarData?.month === monthKey) {
            setCalendarData(nextMonth);
        }
        if (calendarView === 'week' && weekData?.days?.[dayKey]) {
            setWeekData((prev) => ({
                ...prev,
                days: { ...prev.days, [dayKey]: nextDay },
            }));
        }
    };

    const applyCalendarMove = (activity, nextDate) => {
        const previousKey = activity?.__localDayKey
            || (activity?.__timeMs ? dayKeyFromLocalDateObj(new Date(activity.__timeMs)) : null)
            || (getDueAtMs(activity) ? dayKeyFromLocalDateObj(new Date(getDueAtMs(activity))) : null);
        const nextKey = dayKeyFromLocalDateObj(nextDate);
        const nextTimeMs = nextDate.getTime();
        const updated = {
            ...activity,
            dueAt: nextTimeMs,
            dueDate: dayKeyFromLocalDateObj(nextDate),
            dueTime: nextDate.toTimeString().slice(0, 5),
            __localDayKey: nextKey,
            __timeMs: nextTimeMs,
            __bucket: computeClientBucket(activity, nextTimeMs),
        };

        if (previousKey) {
            updateDayInCache(previousKey, (day) => {
                const items = (day.items || []).filter((item) => item.id !== activity.id);
                const counts = recountDay(items);
                return { ...day, items, ...counts };
            });
        }
        updateDayInCache(nextKey, (day) => {
            const items = [...(day.items || []).filter((item) => item.id !== activity.id), updated];
            const counts = recountDay(items);
            return { ...day, items, ...counts };
        });
    };

    const applyCalendarCompletion = (activity, nextStatus) => {
        const prevKey = activity?.__localDayKey
            || (activity?.__timeMs ? dayKeyFromLocalDateObj(new Date(activity.__timeMs)) : null);
        const completedAt = nextStatus === 'completed' ? Date.now() : null;
        const targetDate = completedAt ? new Date(completedAt) : (getDueAtMs(activity) ? new Date(getDueAtMs(activity)) : new Date());
        const nextKey = dayKeyFromLocalDateObj(targetDate);
        const updated = {
            ...activity,
            status: nextStatus,
            completed: nextStatus === 'completed',
            completedAt,
            __timeMs: completedAt || activity.__timeMs,
            __localDayKey: nextKey,
            __bucket: nextStatus === 'completed' ? 'done' : computeClientBucket(activity, targetDate.getTime()),
        };

        if (prevKey) {
            updateDayInCache(prevKey, (day) => {
                const items = (day.items || []).filter((item) => item.id !== activity.id);
                const counts = recountDay(items);
                return { ...day, items, ...counts };
            });
        }
        updateDayInCache(nextKey, (day) => {
            const items = [...(day.items || []).filter((item) => item.id !== activity.id), updated];
            const counts = recountDay(items);
            return { ...day, items, ...counts };
        });
    };

    const handleCalendarReschedule = async (activity, dayKey, timeValue) => {
        if (!activity || !dayKey) return;
        if (activity.status === 'completed') {
            const confirmed = await confirm({
                title: 'Reschedule completed activity-',
                message: 'This activity is completed. Do you want to reschedule it anyway-',
                confirmText: 'Reschedule',
                variant: 'danger',
            });
            if (!confirmed) return;
        }
        const nextDate = combineLocalDateAndTime(dayKey, timeValue || '10:00');
        applyCalendarMove(activity, nextDate);
        try {
            await update(activity.id, {
                dueDate: dayKeyFromLocalDateObj(nextDate),
                dueTime: nextDate.toTimeString().slice(0, 5),
                dueAt: nextDate.getTime(),
            });
            refetch();
        } catch (error) {
            showError('Failed to reschedule');
            refetch();
        }
    };

    const handleCalendarComplete = async (activity) => {
        const isCompleted = activity.status === 'completed';
        applyCalendarCompletion(activity, isCompleted ? 'pending' : 'completed');
        try {
            await update(activity.id, {
                status: isCompleted ? 'pending' : 'completed',
                completed: !isCompleted,
                completedAt: !isCompleted ? Date.now() : null,
            });
            refetch();
            notifyLeadsRefresh();
        } catch (error) {
            showError('Failed to update status');
            refetch();
        }
    };

    const handleCalendarDelete = async (activity) => {
        const confirmed = await confirm({
            title: 'Delete activity',
            message: 'This will remove the activity from the calendar.',
            confirmText: 'Delete',
            variant: 'danger',
        });
        if (!confirmed) return;
        const prevKey = activity?.__localDayKey
            || (activity?.__timeMs ? dayKeyFromLocalDateObj(new Date(activity.__timeMs)) : null);
        if (prevKey) {
            updateDayInCache(prevKey, (day) => {
                const items = (day.items || []).filter((item) => item.id !== activity.id);
                const counts = recountDay(items);
                return { ...day, items, ...counts };
            });
        }
        try {
            await remove(activity.id);
            refetch();
            notifyLeadsRefresh();
        } catch (error) {
            showError('Failed to delete activity');
        }
    };

    const getDefaultTimeValue = (activity) => {
        const dueAtMs = getDueAtMs(activity);
        if (dueAtMs) return new Date(dueAtMs).toTimeString().slice(0, 5);
        return '10:00';
    };

    const handleDragStart = (activity) => (event) => {
        dragActivityRef.current = activity;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', activity.id || '');
    };

    const handleDropOnDay = (dayKey) => async (event) => {
        event.preventDefault();
        const activity = dragActivityRef.current;
        if (!activity) return;
        const timeValue = getDefaultTimeValue(activity);
        await handleCalendarReschedule(activity, dayKey, timeValue);
        dragActivityRef.current = null;
    };

    const handleDropOnSlot = (dayKey, timeValue) => async (event) => {
        event.preventDefault();
        const activity = dragActivityRef.current;
        if (!activity) return;
        await handleCalendarReschedule(activity, dayKey, timeValue);
        dragActivityRef.current = null;
    };

    const allowDrop = (event) => {
        event.preventDefault();
    };


    const handleComplete = async (activity) => {
        const isCompleted = activity.status === 'completed';
        try {
            await update(activity.id, {
                status: isCompleted ? 'pending' : 'completed',
                completed: !isCompleted,
                completedAt: !isCompleted ? Date.now() : null,
            });
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            showError('Failed to update status');
        }
    };

    const handleOutcomeChange = async (activity, outcome) => {
        try {
            await update(activity.id, { outcome });
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            showError('Failed to update outcome');
        }
    };

    const handleReschedule = async (activity, date) => {
        if (!date) return;
        const dueDate = dayKeyFromLocalDateObj(date);
        const dueTime = date.toTimeString().slice(0, 5);
        try {
            await update(activity.id, { dueDate, dueTime, dueAt: date.getTime() });
            setRescheduleOpenId(null);
            refetch();
            notifyLeadsRefresh();
        } catch (err) {
            showError('Failed to reschedule');
        }
    };

    const handleDelete = async (id) => {
        const isConfirmed = await confirm({
            title: 'Delete Follow-up',
            message: 'Are you sure you want to delete this follow-up-',
            confirmText: 'Delete',
            variant: 'danger',
        });

        if (isConfirmed) {
            try {
                await remove(id);
                showSuccess('Follow-up deleted');
                refetch();
                notifyLeadsRefresh();
            } catch (err) {
                showError('Failed to delete follow-up');
            }
        }
    };

    const handleTemplateSelect = (templateId) => {
        const template = TEMPLATES.find((item) => item.id === templateId);
        if (!template) {
            setFormState((prev) => ({ ...prev, templateId }));
            return;
        }
        setFormState((prev) => ({
            ...prev,
            templateId,
            title: template.title,
            description: template.description,
        }));
    };

    const validateForm = () => {
        const nextErrors = {};
        if (!formState.title.trim()) nextErrors.title = 'Title is required';
        if (!formState.entityType) nextErrors.entityType = 'Entity type is required';
        const hasEntityId = formState.entityId.trim();
        const hasMatchHint = Boolean(formState.metaPhone && formState.metaPhone.trim());
        if (!hasEntityId && !hasMatchHint) nextErrors.entityId = 'Entity ID or phone is required';
        if (formState.type !== 'note' && !formState.dueDate) nextErrors.dueDate = 'Due date is required';
        setFormErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const computeReminderAt = (dueAtMs, reminder) => {
        if (!dueAtMs || !reminder) return null;
        let offsetMinutes = 0;
        if (reminder === '30m') offsetMinutes = 30;
        if (reminder === '2h') offsetMinutes = 120;
        if (reminder === '1d') offsetMinutes = 1440;
        return dueAtMs - offsetMinutes * 60000;
    };

    const notifyLeadsRefresh = () => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('crm:leads:refresh'));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm()) return;

        const dueAtMs = formState.dueDate
            ? combineLocalDateAndTime(formState.dueDate, formState.dueTime || '09:00').getTime()
            : null;
        const reminderAt = computeReminderAt(dueAtMs, formState.reminder);
        const meta = {
            ...(editingActivity?.meta || {}),
            project: formState.metaProject || undefined,
            phone: formState.metaPhone || undefined,
            templateId: formState.templateId || undefined,
        };

        const payload = {
            type: formState.type,
            title: formState.title.trim(),
            description: formState.description.trim(),
            entityType: formState.entityType,
            entityId: formState.entityId.trim(),
            entityName: formState.entityName.trim() || undefined,
            dueDate: formState.dueDate || undefined,
            dueTime: formState.dueTime || undefined,
            dueAt: dueAtMs || undefined,
            priority: formState.priority,
            channel: formState.channel,
            outcome: formState.outcome || undefined,
            reminderAt: reminderAt || undefined,
            reminderSent: reminderAt ? false : undefined,
            meta,
            status: formState.status || 'pending',
        };

        try {
            if (editingActivity) {
                await update(editingActivity.id, payload);
                showSuccess('Follow-up updated');
            } else {
                await create(payload);
                showSuccess('Follow-up created');
            }
            setShowModal(false);
            setEditingActivity(null);
            refetch();
            notifyLeadsRefresh();
            notifyLeadsRefresh();
        } catch (err) {
            showError('Failed to save follow-up');
        }
    };

    const renderRowActions = (activity) => {
        const phone = activity?.meta?.phone;
        return (
            <div className="crm-followup-actions">
                <button
                    className="crm-btn crm-btn-secondary crm-btn-sm"
                    onClick={() => phone && window.open(`tel:${phone}`, '_self')}
                    disabled={!phone}
                >
                    Call
                </button>
                <button
                    className="crm-btn crm-btn-secondary crm-btn-sm"
                    onClick={() => window.open(buildWhatsAppUrl(activity), '_blank')}
                >
                    WhatsApp
                </button>
                <button
                    className="crm-btn crm-btn-secondary crm-btn-sm"
                    onClick={() => setRescheduleOpenId((prev) => (prev === activity.id ? null : activity.id))}
                >
                    Reschedule
                </button>
                <button
                    className="crm-btn crm-btn-success crm-btn-sm"
                    onClick={() => handleComplete(activity)}
                >
                    Mark Done
                </button>
            </div>
        );
    };

    const renderReschedule = (activity) => {
        if (rescheduleOpenId !== activity.id) return null;
        const now = new Date();
        const laterToday = new Date();
        laterToday.setHours(now.getHours() + 2, now.getMinutes(), 0, 0);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(9, 0, 0, 0);
        const plusThree = new Date();
        plusThree.setDate(plusThree.getDate() + 3);
        plusThree.setHours(9, 0, 0, 0);

        return (
            <div className="crm-reschedule-panel">
                <button className="crm-btn crm-btn-link" onClick={() => handleReschedule(activity, laterToday)}>
                    Later today
                </button>
                <button className="crm-btn crm-btn-link" onClick={() => handleReschedule(activity, tomorrow)}>
                    Tomorrow
                </button>
                <button className="crm-btn crm-btn-link" onClick={() => handleReschedule(activity, plusThree)}>
                    +3 days
                </button>
                <input
                    type="date"
                    className="crm-input"
                    onChange={(event) => {
                        if (!event.target.value) return;
                        const target = new Date(`${event.target.value}T${activity.dueTime || '09:00'}`);
                        handleReschedule(activity, target);
                    }}
                />
            </div>
        );
    };

    return (
        <div className="crm-container">
            <div className="crm-page-header">
                <div>
                    <h1 className="crm-page-title">Follow-ups</h1>
                    <div className="crm-subtitle crm-activities-subtitle">Stay on top of calls, site visits, and tasks.</div>
                </div>
                <div className="crm-header-actions">
                    <button
                        className="crm-btn crm-btn-primary"
                        onClick={() => {
                            setEditingActivity(null);
                            setPrefillDate(null);
                            setShowModal(true);
                        }}
                    >
                        + Add Follow-up
                    </button>
                </div>
            </div>

            <div className="crm-followup-toolbar">
                <div className="crm-search">
                    <input
                        className="crm-input"
                        placeholder="Search title, entity, phone, email"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                    />
                </div>
            </div>

            <div className="crm-sticky-summary">
                Due: {activityBuckets.summary.due}
                {' - '}
                Overdue: {activityBuckets.summary.overdue}
                {' - '}
                Completed: {activityBuckets.summary.completed}
            </div>

            <div className="crm-tab-bar" style={{ marginTop: '20px' }}>
                {[
                    { id: 'due', label: 'Due' },
                    { id: 'overdue', label: 'Overdue' },
                    { id: 'completed', label: 'Completed' },
                    { id: 'calendar', label: 'Calendar' },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        className={`crm-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                        type="button"
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="crm-loading">
                    <div className="crm-spinner"></div>
                </div>
            ) : (
                <>
                    {activeTab === 'due' && (
                        <div className="crm-followup-sections">
                            <div className="crm-subtitle crm-activities-help" style={{ marginBottom: '12px' }}>
                                Shows due today + upcoming follow-ups. Overdue follow-ups are in Overdue.
                            </div>
                            {[
                                { key: 'today', label: 'Due Today', items: activityBuckets.dueToday },
                                { key: 'upcoming', label: 'Upcoming', items: activityBuckets.upcoming },
                                { key: 'unscheduled', label: 'Unscheduled', items: activityBuckets.unscheduled },
                            ].map(({ key, label, items }) => {
                                if (!items || items.length === 0) return null;
                                const sorted = [...items].sort((a, b) => (getDueAtMs(a) ?? Infinity) - (getDueAtMs(b) ?? Infinity));
                                return (
                                    <div key={key} className="crm-followup-section">
                                        <div className="crm-followup-section-title">
                                            <span>{label}</span>
                                            <span className="count-badge">{items.length}</span>
                                        </div>
                                        <div className="crm-card">
                                            {sorted.map((activity) => {
                                                const dueAt = getDueAtMs(activity);
                                                return (
                                                    <div key={activity.id} className="crm-followup-row">
                                                        <input
                                                            type="checkbox"
                                                            checked={activity.status === 'completed'}
                                                            onChange={() => handleComplete(activity)}
                                                        />
                                                        <div className="crm-followup-icon">{TYPE_ICONS[activity.type] || 'NOTE'}</div>
                                                        <div className="crm-followup-main">
                                                            <div className="crm-followup-title">
                                                                {activity.title}
                                                                {activity.priority && (
                                                                    <span className={`crm-pill priority-${activity.priority}`}>
                                                                        {activity.priority}
                                                                    </span>
                                                                )}
                                                                <span className="crm-pill crm-pill-status due">Due</span>
                                                            </div>
                                                            <div className="crm-followup-meta">
                                                                {activity.entityType && (
                                                                    <button
                                                                        className="crm-link-badge"
                                                                        onClick={() => {
                                                                            const route = getEntityRoute(activity);
                                                                            if (route) history.push(route);
                                                                        }}
                                                                    >
                                                                        {formatEntityLabel(activity)}
                                                                    </button>
                                                                )}
                                                                <span>
                                                                    {dueAt ? formatDate(dueAt) : 'No due time'}
                                                                </span>
                                                            </div>
                                                            {renderReschedule(activity)}
                                                        </div>
                                                        {renderRowActions(activity)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                            {activityBuckets.dueToday.length === 0 &&
                                activityBuckets.upcoming.length === 0 &&
                                activityBuckets.unscheduled.length === 0 && (
                                    <div className="crm-empty-state">
                                        <h3>No follow-ups due</h3>
                                        <p>Add a follow-up or check Overdue for past due items.</p>
                                    </div>
                                )}
                        </div>
                    )}

                    {activeTab === 'overdue' && (
                        <div className="crm-followup-sections">
                            {[
                                { key: 'overdue-today', label: 'Overdue Today', items: activityBuckets.overdueToday },
                                { key: 'overdue-older', label: 'Older Overdue', items: activityBuckets.overdueOlder },
                            ].map(({ key, label, items }) => {
                                if (!items || items.length === 0) return null;
                                const sorted = [...items].sort((a, b) => (getDueAtMs(b) ?? 0) - (getDueAtMs(a) ?? 0));
                                return (
                                    <div key={key} className="crm-followup-section">
                                        <div className="crm-followup-section-title">
                                            <span className="text-danger">{label}</span>
                                            <span className="count-badge">{items.length}</span>
                                        </div>
                                        <div className="crm-card">
                                            {sorted.map((activity) => {
                                                const dueAt = getDueAtMs(activity);
                                                return (
                                                    <div key={activity.id} className="crm-followup-row">
                                                        <input
                                                            type="checkbox"
                                                            checked={activity.status === 'completed'}
                                                            onChange={() => handleComplete(activity)}
                                                        />
                                                        <div className="crm-followup-icon">{TYPE_ICONS[activity.type] || 'NOTE'}</div>
                                                        <div className="crm-followup-main">
                                                            <div className="crm-followup-title">
                                                                {activity.title}
                                                                {activity.priority && (
                                                                    <span className={`crm-pill priority-${activity.priority}`}>
                                                                        {activity.priority}
                                                                    </span>
                                                                )}
                                                                <span className="crm-pill crm-pill-status overdue">Overdue</span>
                                                            </div>
                                                            <div className="crm-followup-meta">
                                                                {activity.entityType && (
                                                                    <button
                                                                        className="crm-link-badge"
                                                                        onClick={() => {
                                                                            const route = getEntityRoute(activity);
                                                                            if (route) history.push(route);
                                                                        }}
                                                                    >
                                                                        {formatEntityLabel(activity)}
                                                                    </button>
                                                                )}
                                                                <span className="text-danger">
                                                                    {dueAt ? formatDate(dueAt) : 'No due time'}
                                                                </span>
                                                            </div>
                                                            {renderReschedule(activity)}
                                                        </div>
                                                        {renderRowActions(activity)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                            {activityBuckets.overdueToday.length === 0 &&
                                activityBuckets.overdueOlder.length === 0 && (
                                    <div className="crm-empty-state">
                                        <h3>No overdue follow-ups</h3>
                                        <p>All caught up. Check Due for upcoming items.</p>
                                    </div>
                                )}
                        </div>
                    )}

                    {activeTab === 'calendar' && (
                        <div className="crm-calendar-wrapper">
                            <div className="crm-calendar-header">
                                <div className="crm-calendar-nav">
                                    <button className="crm-btn crm-btn-secondary" type="button" onClick={handleCalendarPrev}>
                                        Prev
                                    </button>
                                    <button className="crm-btn crm-btn-secondary" type="button" onClick={handleCalendarToday}>
                                        Today
                                    </button>
                                    <button className="crm-btn crm-btn-secondary" type="button" onClick={handleCalendarNext}>
                                        Next
                                    </button>
                                </div>
                                <div className="crm-calendar-title">
                                    {getMonthDateFromKey(calendarMonth).toLocaleDateString('en-US', {
                                        month: 'long',
                                        year: 'numeric',
                                    })}
                                </div>
                                <div className="crm-calendar-view-toggle">
                                    <button
                                        className={`crm-btn crm-btn-secondary ${calendarView === 'month' ? 'active' : ''}`}
                                        type="button"
                                        onClick={() => setCalendarView('month')}
                                    >
                                        Month
                                    </button>
                                    <button
                                        className={`crm-btn crm-btn-secondary ${calendarView === 'week' ? 'active' : ''}`}
                                        type="button"
                                        onClick={() => setCalendarView('week')}
                                    >
                                        Week
                                    </button>
                                </div>
                            </div>

                            <div className="crm-calendar-body">
                                <div className="crm-calendar-grid">
                                    {calendarView === 'month' && (
                                        <>
                                            <div className="crm-calendar-weekdays">
                                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
                                                    <div key={label} className="crm-calendar-weekday">
                                                        {label}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="crm-calendar-cells">
                                                {monthGridDays.map((date) => {
                                                    const dayKey = dayKeyFromLocalDateObj(date);
                                                    const bucket = getDayBucket(dayKey);
                                                    const isCurrentMonth = date.getMonth() === getMonthDateFromKey(calendarMonth).getMonth();
                                                    const isSelected = dayKey === activeDayKey;
                                                    const isToday = dayKey === todayDayKey;
                                                    const totalCount = (bucket.due || 0) + (bucket.overdue || 0) + (bucket.completed || 0);
                                                    const heatLevel = totalCount >= 4 ? 'heat-4' : totalCount === 3 ? 'heat-3' : totalCount === 2 ? 'heat-2' : totalCount === 1 ? 'heat-1' : '';
                                                    return (
                                                        <div
                                                            key={dayKey}
                                                            className={`crm-calendar-cell ${isCurrentMonth ? '' : 'muted'} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${heatLevel}`}
                                                            onClick={() => handleSelectDay(dayKey)}
                                                            onDragOver={allowDrop}
                                                            onDrop={handleDropOnDay(dayKey)}
                                                        >
                                                            <div className="crm-calendar-day-top">
                                                                <span className="crm-calendar-day-number">{date.getDate()}</span>
                                                                <div className="crm-calendar-badges">
                                                                    {bucket.overdue > 0 && (
                                                                        <span className="crm-badge crm-badge-overdue">OVERDUE {bucket.overdue}</span>
                                                                    )}
                                                                    {bucket.due > 0 && (
                                                                        <span className="crm-badge crm-badge-due">DUE {bucket.due}</span>
                                                                    )}
                                                                    {bucket.completed > 0 && (
                                                                        <span className="crm-badge crm-badge-done">DONE {bucket.completed}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}

                                    {calendarView === 'week' && (
                                        <div className="crm-week-grid">
                                            <div className="crm-week-header">
                                                <div className="crm-week-time-spacer" />
                                                {weekDays.map((date) => {
                                                    const dayKey = dayKeyFromLocalDateObj(date);
                                                    const bucket = getDayBucket(dayKey);
                                                    const isSelected = dayKey === activeDayKey;
                                                    const isToday = dayKey === todayDayKey;
                                                    return (
                                                        <div
                                                            key={dayKey}
                                                            className={`crm-week-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                                                            onClick={() => handleSelectDay(dayKey)}
                                                        >
                                                            <div className="crm-week-day-label">
                                                                {date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}
                                                            </div>
                                                            <div className="crm-calendar-badges">
                                                                {bucket.overdue > 0 && (
                                                                    <span className="crm-badge crm-badge-overdue">OVERDUE {bucket.overdue}</span>
                                                                )}
                                                                {bucket.due > 0 && (
                                                                    <span className="crm-badge crm-badge-due">DUE {bucket.due}</span>
                                                                )}
                                                                {bucket.completed > 0 && (
                                                                    <span className="crm-badge crm-badge-done">DONE {bucket.completed}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="crm-week-body">
                                                <div className="crm-week-times">
                                                    {weekSlots.map((slot) => (
                                                        <div key={slot} className="crm-week-time">
                                                            {slot}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="crm-week-columns">
                                                    {weekDays.map((date) => {
                                                        const dayKey = dayKeyFromLocalDateObj(date);
                                                        const bucket = getDayBucket(dayKey);
                                                        const itemsBySlot = (bucket.items || []).reduce((acc, item) => {
                                                            const timeMs = item.__timeMs || getActivityTimeMs(item);
                                                            if (!timeMs) return acc;
                                                            const time = new Date(timeMs);
                                                            const slotKey = `${pad2(time.getHours())}:${time.getMinutes() >= 30 ? '30' : '00'}`;
                                                            acc[slotKey] = acc[slotKey] || [];
                                                            acc[slotKey].push(item);
                                                            return acc;
                                                        }, {});
                                                        return (
                                                            <div key={dayKey} className="crm-week-column">
                                                                {weekSlots.map((slot) => (
                                                                    <div
                                                                        key={`${dayKey}-${slot}`}
                                                                        className="crm-week-slot"
                                                                        onDragOver={allowDrop}
                                                                        onDrop={handleDropOnSlot(dayKey, slot)}
                                                                    >
                                                                        {(itemsBySlot[slot] || []).map((item) => (
                                                                            <div
                                                                                key={item.id}
                                                                                className={`crm-week-item ${item.__bucket || ''}`}
                                                                                draggable
                                                                                onDragStart={handleDragStart(item)}
                                                                            >
                                                                                <div className="crm-week-item-time">{formatTimeLocal(new Date(item.__timeMs || Date.now()))}</div>
                                                                                <div className="crm-week-item-title">{item.title}</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className={`crm-calendar-agenda ${agendaOpen && isMobileCalendar ? 'open' : ''}`}>
                                    <div className="crm-calendar-agenda-header">
                                        <div className="crm-calendar-agenda-title">
                                            {parseDayKeyToLocalDate(activeDayKey).toLocaleDateString('en-US', {
                                                weekday: 'short',
                                                month: 'short',
                                                day: 'numeric',
                                            })}
                                        </div>
                                        <div className="crm-calendar-agenda-count">
                                            {selectedDayItems.length} items
                                        </div>
                                    </div>
                                    <div className="crm-calendar-agenda-list">
                                        {calendarLoading && <div className="crm-muted">Loading...</div>}
                                        {!calendarLoading && selectedDayItems.length === 0 && (
                                            <div className="crm-empty-state">
                                                <p>No activities for this day.</p>
                                                <button
                                                    className="crm-btn crm-btn-secondary"
                                                    type="button"
                                                    onClick={() => openCreateFollowUp(parseDayKeyToLocalDate(activeDayKey))}
                                                >
                                                    + Add Follow-up
                                                </button>
                                            </div>
                                        )}
                                        {selectedDayItems.map((activity) => {
                                            const bucket = activity.__bucket || computeClientBucket(activity, activity.__timeMs || Date.now());
                                            const timeMs = activity.__timeMs || getActivityTimeMs(activity);
                                            const scheduledAt = timeMs ? new Date(timeMs) : null;
                                            const completedAt = activity.completedAt ? new Date(activity.completedAt) : null;
                                            return (
                                                <div key={activity.id} className="crm-calendar-card" draggable onDragStart={handleDragStart(activity)}>
                                                    <div className="crm-calendar-card-header">
                                                        <span className="crm-calendar-time">
                                                            {scheduledAt ? formatTimeLocal(scheduledAt) : '—'}
                                                        </span>
                                                        <span className="crm-calendar-title-text">{activity.title}</span>
                                                        <span className={`crm-pill crm-pill-status ${bucket}`}>
                                                            {bucket === 'done' ? 'Completed' : bucket === 'overdue' ? 'Overdue' : 'Due'}
                                                        </span>
                                                    </div>
                                                    <div className="crm-calendar-card-meta">
                                                        <span>{formatEntityLabel(activity)}</span>
                                                    </div>
                                                    <div className="crm-calendar-card-meta">
                                                        {bucket === 'done' && completedAt && (
                                                            <span>Done: {completedAt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                                                        )}
                                                        {bucket !== 'done' && scheduledAt && (
                                                            <span>Scheduled: {scheduledAt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                                                        )}
                                                    </div>
                                                    <div className="crm-calendar-card-actions">
                                                        <button
                                                            className="crm-btn crm-btn-secondary"
                                                            type="button"
                                                            onClick={() => {
                                                                const route = getEntityRoute(activity);
                                                                if (route) history.push(route);
                                                            }}
                                                        >
                                                            Open entity
                                                        </button>
                                                        {bucket !== 'done' && (
                                                            <button
                                                                className="crm-btn crm-btn-primary"
                                                                type="button"
                                                                onClick={() => handleCalendarComplete(activity)}
                                                            >
                                                                Mark Done
                                                            </button>
                                                        )}
                                                        <button
                                                            className="crm-btn crm-btn-secondary"
                                                            type="button"
                                                            onClick={() => handleCalendarDelete(activity)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                    {bucket !== 'done' && (
                                                        <div className="crm-calendar-card-reschedule">
                                                            <input
                                                                type="date"
                                                                className="crm-input"
                                                                defaultValue={activity.__localDayKey}
                                                                onBlur={(event) => handleCalendarReschedule(activity, event.target.value || activeDayKey, getDefaultTimeValue(activity))}
                                                            />
                                                            <input
                                                                type="time"
                                                                className="crm-input"
                                                                defaultValue={getDefaultTimeValue(activity)}
                                                                onBlur={(event) => handleCalendarReschedule(activity, activity.__localDayKey || activeDayKey, event.target.value)}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}



                    {
                        activeTab === 'completed' && (
                            <div className="crm-completed">
                                <div className="crm-filter-row">
                                    <select
                                        className="crm-select"
                                        value={completedRange}
                                        onChange={(event) => setCompletedRange(event.target.value)}
                                    >
                                        <option value="today">Today</option>
                                        <option value="7">Last 7 days</option>
                                        <option value="all">All</option>
                                    </select>
                                </div>
                                {completedFiltered.length === 0 ? (
                                    <div className="crm-empty-state">
                                        <h3>No completed follow-ups</h3>
                                        <p>Completed activities will show up here.</p>
                                    </div>
                                ) : (
                                    <div className="crm-card">
                                        {[...completedFiltered]
                                            .sort((a, b) => (getCompletedAtMs(b) ?? 0) - (getCompletedAtMs(a) ?? 0))
                                            .map((activity) => (
                                                <div key={activity.id} className="crm-followup-row compact">
                                                    <div className="crm-followup-icon">{TYPE_ICONS[activity.type] || 'NOTE'}</div>
                                                    <div className="crm-followup-main">
                                                        <div className="crm-followup-title">
                                                            {activity.title}
                                                            <span className="crm-pill crm-pill-status completed">Completed</span>
                                                        </div>
                                                        <div className="crm-followup-meta">
                                                            {formatEntityLabel(activity)}{' - '}{formatDate(activity.completedAt || activity.updatedAt)}
                                                        </div>
                                                    </div>
                                                    <select
                                                        className="crm-select crm-select-sm"
                                                        value={activity.outcome || ''}
                                                        onChange={(event) => handleOutcomeChange(activity, event.target.value)}
                                                    >
                                                        {OUTCOME_OPTIONS.map((opt) => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                                        onClick={() => {
                                                            setEditingActivity(activity);
                                                            setShowModal(true);
                                                        }}
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        )
                    }


                    {
                        showModal && (
                            <div className="crm-modal-overlay" onClick={() => setShowModal(false)}>
                                <div
                                    className="crm-modal"
                                    onClick={(event) => event.stopPropagation()}
                                    style={{ maxWidth: '720px', width: '100%', maxHeight: '90vh', overflow: 'auto', background: 'var(--crm-bg)' }}
                                >
                                    <div className="crm-modal-header">
                                        <h2 className="crm-modal-title">{editingActivity ? 'Edit Follow-up' : 'Add Follow-up'}</h2>
                                        <button className="crm-modal-close" onClick={() => setShowModal(false)}>-</button>
                                    </div>
                                    <div className="crm-modal-body">
                                        <div className="crm-tab-bar" style={{ padding: 0, marginBottom: '12px' }}>
                                            {[
                                                { id: 'smart', label: 'Smart Actions' },
                                                { id: 'log', label: 'Log Activity' },
                                            ].map((tab) => (
                                                <button
                                                    key={tab.id}
                                                    className={`crm-tab ${modalTab === tab.id ? 'active' : ''}`}
                                                    onClick={() => setModalTab(tab.id)}
                                                    type="button"
                                                >
                                                    {tab.label}
                                                </button>
                                            ))}
                                        </div>

                                        {modalTab === 'smart' && (
                                            <div className="crm-form-group">
                                                <label className="crm-label">Smart Actions</label>
                                                <SmartActionsBar {...buildModalSmartActionsProps()} />
                                            </div>
                                        )}

                                        {modalTab === 'log' && (
                                            <form onSubmit={handleSubmit}>
                                                <div className="crm-form-row">
                                                    <div className="crm-form-group">
                                                        <label className="crm-label required">Type</label>
                                                        <select
                                                            className="crm-select"
                                                            value={formState.type}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, type: event.target.value }))}
                                                        >
                                                            {TYPE_OPTIONS.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Template</label>
                                                        <select
                                                            className="crm-select"
                                                            value={formState.templateId}
                                                            onChange={(event) => handleTemplateSelect(event.target.value)}
                                                        >
                                                            <option value="">Select template</option>
                                                            {TEMPLATES.map((template) => (
                                                                <option key={template.id} value={template.id}>
                                                                    {template.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="crm-form-group">
                                                    <label className="crm-label required">Title</label>
                                                    <input
                                                        className={`crm-input ${formErrors.title ? 'error' : ''}`}
                                                        value={formState.title}
                                                        onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                                                    />
                                                    {formErrors.title && <span className="crm-error">{formErrors.title}</span>}
                                                </div>

                                                <div className="crm-form-row">
                                                    <div className="crm-form-group">
                                                        <label className="crm-label required">Entity type</label>
                                                        <select
                                                            className={`crm-select ${formErrors.entityType ? 'error' : ''}`}
                                                            value={formState.entityType}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, entityType: event.target.value }))}
                                                        >
                                                            {ENTITY_OPTIONS.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="crm-form-group">
                                                        <label className="crm-label required">Entity ID</label>
                                                        <input
                                                            className={`crm-input ${formErrors.entityId ? 'error' : ''}`}
                                                            value={formState.entityId}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, entityId: event.target.value }))}
                                                        />
                                                        {formErrors.entityId && <span className="crm-error">{formErrors.entityId}</span>}
                                                    </div>
                                                </div>

                                                <div className="crm-form-row">
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Entity Name</label>
                                                        <input
                                                            className="crm-input"
                                                            value={formState.entityName}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, entityName: event.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Phone</label>
                                                        <input
                                                            className="crm-input"
                                                            value={formState.metaPhone}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, metaPhone: event.target.value }))}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="crm-form-row">
                                                    <div className="crm-form-group">
                                                        <label className={`crm-label ${formState.type !== 'note' ? 'required' : ''}`}>Due Date</label>
                                                        <input
                                                            type="date"
                                                            className={`crm-input ${formErrors.dueDate ? 'error' : ''}`}
                                                            value={formState.dueDate}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, dueDate: event.target.value }))}
                                                        />
                                                        {formErrors.dueDate && <span className="crm-error">{formErrors.dueDate}</span>}
                                                    </div>
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Due Time</label>
                                                        <input
                                                            type="time"
                                                            className="crm-input"
                                                            value={formState.dueTime}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, dueTime: event.target.value }))}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="crm-form-row">
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Priority</label>
                                                        <select
                                                            className="crm-select"
                                                            value={formState.priority}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, priority: event.target.value }))}
                                                        >
                                                            {PRIORITY_OPTIONS.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Channel</label>
                                                        <select
                                                            className="crm-select"
                                                            value={formState.channel}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, channel: event.target.value }))}
                                                        >
                                                            {CHANNEL_OPTIONS.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="crm-form-row">
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Outcome</label>
                                                        <select
                                                            className="crm-select"
                                                            value={formState.outcome}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, outcome: event.target.value }))}
                                                        >
                                                            {OUTCOME_OPTIONS.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="crm-form-group">
                                                        <label className="crm-label">Reminder</label>
                                                        <select
                                                            className="crm-select"
                                                            value={formState.reminder}
                                                            onChange={(event) => setFormState((prev) => ({ ...prev, reminder: event.target.value }))}
                                                        >
                                                            {REMINDER_OPTIONS.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="crm-form-group">
                                                    <label className="crm-label">Project</label>
                                                    <input
                                                        className="crm-input"
                                                        value={formState.metaProject}
                                                        onChange={(event) => setFormState((prev) => ({ ...prev, metaProject: event.target.value }))}
                                                    />
                                                </div>

                                                <div className="crm-form-group">
                                                    <label className="crm-label">Notes / Description</label>
                                                    <textarea
                                                        className="crm-textarea"
                                                        rows={4}
                                                        value={formState.description}
                                                        onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                                                    />
                                                </div>

                                                <div className="crm-form-actions">
                                                    <button
                                                        type="button"
                                                        className="crm-btn crm-btn-secondary"
                                                        onClick={() => setShowModal(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        className="crm-btn crm-btn-primary"
                                                        disabled={mutationLoading}
                                                    >
                                                        {mutationLoading ? 'Saving...' : 'Save Follow-up'}
                                                    </button>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {
                        showSmartFollowupModal && (
                            <div
                                className="crm-modal-overlay"
                                onClick={() => {
                                    setShowSmartFollowupModal(false);
                                    setSmartActionActivity(null);
                                }}
                            >
                                <div
                                    className="crm-modal"
                                    onClick={(event) => event.stopPropagation()}
                                    style={{ background: 'var(--crm-bg)', maxWidth: '420px', width: '100%' }}
                                >
                                    <div className="crm-modal-header">
                                        <h2 className="crm-modal-title">Schedule Follow-up</h2>
                                        <button
                                            className="crm-modal-close"
                                            onClick={() => {
                                                setShowSmartFollowupModal(false);
                                                setSmartActionActivity(null);
                                            }}
                                        >
                                            X
                                        </button>
                                    </div>
                                    <div className="crm-modal-body">
                                        <div className="crm-form-row">
                                            <div className="crm-form-group">
                                                <label className="crm-label required">Date</label>
                                                <input
                                                    type="date"
                                                    className="crm-input"
                                                    value={smartFollowupDraft.date}
                                                    onChange={(event) => setSmartFollowupDraft((prev) => ({ ...prev, date: event.target.value }))}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Time</label>
                                                <input
                                                    type="time"
                                                    className="crm-input"
                                                    value={smartFollowupDraft.time}
                                                    onChange={(event) => setSmartFollowupDraft((prev) => ({ ...prev, time: event.target.value }))}
                                                />
                                            </div>
                                        </div>

                                        <div className="crm-form-actions">
                                            <button
                                                className="crm-btn crm-btn-secondary"
                                                onClick={() => {
                                                    setShowSmartFollowupModal(false);
                                                    setSmartActionActivity(null);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="crm-btn crm-btn-primary"
                                                onClick={handleSmartFollowupSave}
                                            >
                                                Save Follow-up
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {
                        showSmartSiteVisitModal && (
                            <div
                                className="crm-modal-overlay"
                                onClick={() => {
                                    setShowSmartSiteVisitModal(false);
                                    setSmartActionActivity(null);
                                }}
                            >
                                <div
                                    className="crm-modal"
                                    onClick={(event) => event.stopPropagation()}
                                    style={{ background: 'var(--crm-bg)', maxWidth: '520px', width: '100%' }}
                                >
                                    <div className="crm-modal-header">
                                        <h2 className="crm-modal-title">Schedule Site Visit</h2>
                                        <button
                                            className="crm-modal-close"
                                            onClick={() => {
                                                setShowSmartSiteVisitModal(false);
                                                setSmartActionActivity(null);
                                            }}
                                        >
                                            X
                                        </button>
                                    </div>
                                    <div className="crm-modal-body">
                                        <div className="crm-form-row">
                                            <div className="crm-form-group">
                                                <label className="crm-label required">Date</label>
                                                <input
                                                    type="date"
                                                    className="crm-input"
                                                    value={smartSiteVisitDraft.date}
                                                    onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, date: event.target.value }))}
                                                />
                                            </div>
                                            <div className="crm-form-group">
                                                <label className="crm-label">Time</label>
                                                <input
                                                    type="time"
                                                    className="crm-input"
                                                    value={smartSiteVisitDraft.time}
                                                    onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, time: event.target.value }))}
                                                />
                                            </div>
                                        </div>

                                        <div className="crm-form-group">
                                            <label className="crm-label">Location</label>
                                            <input
                                                type="text"
                                                className="crm-input"
                                                value={smartSiteVisitDraft.location}
                                                onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, location: event.target.value }))}
                                                placeholder="Property address or meeting point"
                                            />
                                        </div>

                                        <div className="crm-form-group">
                                            <label className="crm-label">Attendees</label>
                                            <input
                                                type="text"
                                                className="crm-input"
                                                value={smartSiteVisitDraft.attendees}
                                                onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, attendees: event.target.value }))}
                                                placeholder="comma separated names"
                                            />
                                        </div>

                                        <div className="crm-form-group">
                                            <label className="crm-label">Notes</label>
                                            <textarea
                                                className="crm-textarea"
                                                rows={4}
                                                value={smartSiteVisitDraft.notes}
                                                onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, notes: event.target.value }))}
                                                placeholder="Any notes for the visit"
                                            />
                                        </div>

                                        <div className="crm-form-actions">
                                            <button
                                                className="crm-btn crm-btn-secondary"
                                                onClick={() => {
                                                    setShowSmartSiteVisitModal(false);
                                                    setSmartActionActivity(null);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="crm-btn crm-btn-primary"
                                                onClick={handleSmartSiteVisitSave}
                                            >
                                                Save Visit
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    <ConfirmDialog />
                </>
            )}
        </div >
    );
};

export default ActivitiesPage;
